import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Search, X } from 'lucide-react';
import { getActivityInfo } from '../../constants/activityTypes';
import WorkoutDetailsModal from '../workout/WorkoutDetailsModal';
import { getExerciseAnimationUrl } from '../../utils/exerciseImages';
import CycleSnapshotSection from '../cycles/CycleSnapshotSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============================================
// CLIENT METRICS DASHBOARD
// Interactive, visual, explorable
// ONLY is_finished=true workouts
// 100% Spanish UI
// ============================================

// Period options
const PERIOD_OPTIONS = [
  { value: '7d', label: 'Últimos 7 días' },
  { value: '30d', label: 'Últimos 30 días' },
  { value: '90d', label: 'Últimos 90 días' },
  { value: 'all', label: 'Todo el histórico' }
];

// Trend indicator
const TrendIndicator = ({ trend, size = 'md' }) => {
  const sizeClass = size === 'lg' ? 'text-xl' : 'text-lg';
  if (trend === 'up') return <span className={`text-green-400 font-bold ${sizeClass}`}>↗</span>;
  if (trend === 'down') return <span className={`text-red-400 font-bold ${sizeClass}`}>↘</span>;
  return <span className={`text-blue-400 ${sizeClass}`}>→</span>;
};

// ============================================
// EXERCISE PROGRESSION CHART MODAL (Reusable)
// Used by both PR cards and Exercise Insight cards
// ============================================
const ExerciseProgressionModal = ({ exercise, onClose }) => {
  if (!exercise) return null;
  
  const { name, progress, image_url, best_weight, best_reps, best_date, session_count } = exercise;
  
  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div 
        className="glass-card max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with GIF */}
        <div className="flex items-start gap-4 mb-6">
          {image_url && (
            <img 
              src={image_url} 
              alt={name}
              className="w-20 h-20 object-cover rounded-lg bg-gray-800"
            />
          )}
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-1">{name}</h3>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-purple-300">🏆 PR: {best_reps} × {best_weight} kg</span>
              <span className="text-blue-300">📅 {best_date}</span>
              <span className="text-green-300">📊 {session_count} sesiones</span>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl leading-none"
          >
            ×
          </button>
        </div>
        
        {/* Progression Chart - Single Volume Line for Clarity */}
        <div className="bg-gray-800/50 rounded-lg p-4">
          <h4 className="text-white font-semibold mb-3">📈 Progresión de volumen</h4>
          {progress && progress.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={progress.map(p => ({
                ...p,
                volume: (p.weight || 0) * (p.reps || 0)
              }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.5} />
                <XAxis 
                  dataKey="date" 
                  stroke="#9CA3AF" 
                  fontSize={11}
                  tickFormatter={(val) => {
                    const parts = val.split('-');
                    return parts.length >= 3 ? `${parts[1]}/${parts[2]}` : val;
                  }}
                />
                <YAxis 
                  stroke="#9CA3AF" 
                  fontSize={11}
                  domain={['auto', 'auto']}
                  tickFormatter={(val) => `${val}`}
                />
                <Tooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      const weight = data.weight || '-';
                      const reps = data.reps || '-';
                      const volume = data.volume || 0;
                      return (
                        <div className="bg-gray-800 border border-gray-600 rounded-lg p-3 shadow-lg">
                          <p className="text-gray-400 text-xs mb-2">{label}</p>
                          <p className="text-purple-300 font-bold text-lg mb-1">
                            {volume.toLocaleString()} kg
                          </p>
                          <p className="text-blue-200 text-sm">
                            {weight} kg × {reps} reps
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                {/* Single Volume Line - Clean and Clear */}
                <Line 
                  type="monotone" 
                  dataKey="volume" 
                  stroke="#A78BFA" 
                  strokeWidth={3}
                  dot={{ fill: '#A78BFA', r: 5, strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 8, fill: '#8B5CF6' }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-blue-400 text-center py-8">Sin datos de progresión</p>
          )}
        </div>
        
        {/* Session Details Table */}
        {progress && progress.length > 0 && (
          <div className="mt-4">
            <h4 className="text-white font-semibold mb-2">📋 Historial de sesiones</h4>
            <div className="bg-gray-800/30 rounded-lg overflow-hidden">
              <div className="max-h-40 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-700/50 sticky top-0">
                    <tr>
                      <th className="text-left text-blue-300 p-2">Fecha</th>
                      <th className="text-right text-blue-300 p-2">Peso</th>
                      <th className="text-right text-blue-300 p-2">Reps</th>
                      <th className="text-right text-blue-300 p-2">Volumen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...progress].reverse().map((session, idx) => (
                      <tr key={idx} className="border-t border-gray-700/50">
                        <td className="text-white p-2">{session.date}</td>
                        <td className="text-white text-right p-2">{session.weight} kg</td>
                        <td className="text-white text-right p-2">{session.reps}</td>
                        <td className="text-purple-300 text-right p-2">{session.volume} kg</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// SUMMARY CARD
// ============================================
const SummaryCard = ({ icon, label, value, unit, color = 'blue' }) => {
  const colorClasses = {
    blue: 'border-blue-400 bg-blue-900/20',
    green: 'border-green-400 bg-green-900/20',
    purple: 'border-purple-400 bg-purple-900/20',
    yellow: 'border-yellow-400 bg-yellow-900/20',
    orange: 'border-orange-400 bg-orange-900/20'
  };
  
  return (
    <div className={`glass-card p-4 border-l-4 ${colorClasses[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xl">{icon}</span>
        <span className="text-blue-300 text-sm">{label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold text-white">{value}</span>
        {unit && <span className="text-blue-400 text-sm">{unit}</span>}
      </div>
    </div>
  );
};

// ============================================
// PROGRESSION CARD (Phase 1: merged PR + Exercise Insights)
// Single canonical surface per exercise: best lift + recent trend +
// last/sessions count. Click → full progression modal.
// ============================================
const ProgressionCard = ({ exercise, record, onClick }) => {
  // Inputs are intentionally flexible: ProgressionCard can be hydrated
  // from either the PR list (`record`) or the exercise_details list
  // (`exercise`). We merge into a single visual.
  const name = exercise?.name || record?.exercise || '';
  const exerciseId = exercise?.exercise_id || record?.exercise_id;
  const image = exercise?.animation_webp || exercise?.image_url || record?.image_url;
  const bestWeight = exercise?.best_weight ?? record?.weight;
  const bestReps = exercise?.best_reps ?? record?.reps;
  const lastWeight = exercise?.last_weight;
  const sessionCount = exercise?.session_count;
  const trend = exercise?.trend;
  const lastDate = exercise?.last_date || record?.date;

  return (
    <div
      className="glass-card p-3 hover:bg-purple-900/20 transition cursor-pointer border border-purple-500/15 hover:border-purple-400/40 group"
      onClick={onClick}
      data-testid={`progression-card-${exerciseId || name}`}
    >
      <div className="flex items-center gap-3">
        {image ? (
          <img
            src={getExerciseAnimationUrl({ animation_webp: exercise?.animation_webp, image_url: image })}
            alt={name}
            className="w-14 h-14 object-cover rounded-lg bg-gray-800 flex-shrink-0"
          />
        ) : (
          <div className="w-14 h-14 rounded-lg bg-gray-800 flex items-center justify-center flex-shrink-0">
            <span className="text-2xl">🏋️</span>
          </div>
        )}

        <div className="flex-1 min-w-0">
          <h4 className="text-white font-semibold text-sm truncate mb-1">{name}</h4>
          <div className="flex items-baseline gap-2 mb-0.5">
            <span className="text-purple-300 font-bold text-base">
              {bestReps ?? '-'} × {bestWeight ?? '-'} kg
            </span>
            <span className="text-purple-400/70 text-[10px] uppercase tracking-wider">mejor</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-blue-300/80">
            {lastWeight != null && <span>Último: {lastWeight} kg</span>}
            {sessionCount != null && <span className="text-blue-400/60">· {sessionCount} ses.</span>}
            {lastDate && <span className="text-blue-400/50 hidden sm:inline">· {lastDate}</span>}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {trend && <TrendIndicator trend={trend} size="md" />}
          <span className="text-purple-400/70 opacity-0 group-hover:opacity-100 transition text-sm">📈</span>
        </div>
      </div>
    </div>
  );
};

// ============================================
// RECENT WORKOUTS SECTION (P1)
// Shows completed workouts ordered by date DESC
// ERP Phase 4.8: Accordion-style expandable rows
// Multiple rows can be expanded simultaneously
// ============================================
const RecentWorkoutsSection = ({ workouts, onViewDetails, isLoading }) => {
  // Track which workouts are expanded (multiple allowed)
  const [expandedWorkouts, setExpandedWorkouts] = useState(new Set());
  // Cache for workout details to avoid refetching
  const [workoutDetailsCache, setWorkoutDetailsCache] = useState({});
  const [loadingDetails, setLoadingDetails] = useState(null);
  
  if (!workouts || workouts.length === 0) {
    return null;
  }
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Toggle accordion expansion (allows multiple expanded)
  const handleToggleExpand = async (workout) => {
    const workoutId = workout.id;
    
    // Toggle this workout's expanded state
    setExpandedWorkouts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(workoutId)) {
        newSet.delete(workoutId);
      } else {
        newSet.add(workoutId);
      }
      return newSet;
    });
    
    // Check if we already have details cached or if workout already has exercises
    if (workoutDetailsCache[workoutId] || (workout.exercises && workout.exercises.length > 0)) {
      return; // Data already available
    }
    
    // Lazy load workout details
    setLoadingDetails(workoutId);
    try {
      const response = await axios.get(`${API}/workouts/${workoutId}`);
      setWorkoutDetailsCache(prev => ({
        ...prev,
        [workoutId]: response.data
      }));
    } catch (error) {
      console.error('Error loading workout details:', error);
    } finally {
      setLoadingDetails(null);
    }
  };

  // Expand all workouts
  const handleExpandAll = async () => {
    const allIds = new Set(workouts.map(w => w.id));
    setExpandedWorkouts(allIds);
    
    // Load details for workouts that don't have cached data
    for (const workout of workouts) {
      if (!workoutDetailsCache[workout.id] && !(workout.exercises && workout.exercises.length > 0)) {
        try {
          const response = await axios.get(`${API}/workouts/${workout.id}`);
          setWorkoutDetailsCache(prev => ({
            ...prev,
            [workout.id]: response.data
          }));
        } catch (error) {
          console.error('Error loading workout details:', error);
        }
      }
    }
  };

  // Collapse all workouts
  const handleCollapseAll = () => {
    setExpandedWorkouts(new Set());
  };

  // Get workout details (from cache, props, or loading state)
  const getWorkoutDetails = (workout) => {
    if (workoutDetailsCache[workout.id]) {
      return workoutDetailsCache[workout.id];
    }
    if (workout.exercises && workout.exercises.length > 0) {
      return workout;
    }
    return null;
  };

  const hasExpandedWorkouts = expandedWorkouts.size > 0;

  return (
    <div className="glass-card p-4" data-testid="recent-workouts-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold text-lg">📋 Entrenamientos recientes</h3>
        <div className="flex gap-2">
          <button
            onClick={handleExpandAll}
            className="text-xs px-3 py-1.5 rounded-lg bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 transition"
            title="Expandir todos"
          >
            ⬇ Expandir
          </button>
          {hasExpandedWorkouts && (
            <button
              onClick={handleCollapseAll}
              className="text-xs px-3 py-1.5 rounded-lg bg-gray-500/20 text-gray-300 hover:bg-gray-500/30 transition"
              title="Colapsar todos"
            >
              ⬆ Colapsar
            </button>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {workouts.map((workout, idx) => {
          const isExpanded = expandedWorkouts.has(workout.id);
          const isLoadingThis = loadingDetails === workout.id;
          const details = getWorkoutDetails(workout);
          
          return (
            <div 
              key={workout.id || idx}
              className={`bg-blue-900/20 rounded-lg border transition-all duration-200 ${
                isExpanded 
                  ? 'border-blue-400/60 bg-blue-900/30' 
                  : 'border-blue-500/20 hover:border-blue-400/40'
              }`}
              data-testid={`recent-workout-${workout.id}`}
            >
              {/* Collapsed Header Row */}
              <div 
                className="p-3 cursor-pointer"
                onClick={() => handleToggleExpand(workout)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {/* Chevron Icon */}
                    <button
                      className={`text-blue-300 hover:text-white transition-transform duration-200 ${
                        isExpanded ? 'rotate-90' : ''
                      }`}
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        className="h-5 w-5" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <div className="bg-green-500/20 text-green-300 px-2 py-1 rounded text-xs font-medium">
                      Completado
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{workout.template_name || 'Entrenamiento'}</p>
                      <p className="text-blue-400 text-xs">{formatDate(workout.date)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-right">
                    <div>
                      <p className="text-purple-300 font-semibold text-sm">
                        {workout.total_volume >= 1000 
                          ? `${(workout.total_volume / 1000).toFixed(1)} ton` 
                          : `${Math.round(workout.total_volume || 0)} kg`}
                      </p>
                      <p className="text-blue-400 text-xs">Volumen</p>
                    </div>
                    <div>
                      <p className="text-blue-200 font-semibold text-sm">
                        {workout.duration > 0 ? `${Math.round(workout.duration)} min` : '-'}
                      </p>
                      <p className="text-blue-400 text-xs">Duración</p>
                    </div>
                    <div>
                      <p className="text-yellow-300 font-semibold text-sm">
                        {workout.exercise_count || (workout.exercises?.length) || 0} ej.
                      </p>
                      <p className="text-blue-400 text-xs">Ejercicios</p>
                    </div>
                    {workout.session_rating && (
                      <div>
                        <p className="text-green-300 font-semibold text-sm">
                          {workout.session_rating}/10
                        </p>
                        <p className="text-blue-400 text-xs">Rating</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Expanded Content */}
              {isExpanded && (
                <div className="border-t border-blue-500/30 p-4 bg-blue-950/30">
                  {isLoadingThis ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
                      <span className="ml-2 text-blue-300 text-sm">Cargando detalles...</span>
                    </div>
                  ) : details && details.exercises && details.exercises.length > 0 ? (
                    <div className="space-y-1.5">
                      {/* Compact Exercise List - One line per exercise, reduced spacing */}
                      {details.exercises.map((exercise, exIdx) => {
                        const actualSets = exercise.actual_sets || [];
                        const completedSets = actualSets.filter(s => s.completed !== false);
                        const hasCompletedSets = completedSets.length > 0;
                        
                        // Find PR set index (highest weight, then highest reps as tiebreaker)
                        let prSetIndex = -1;
                        if (hasCompletedSets) {
                          let maxWeight = 0;
                          let maxRepsAtWeight = 0;
                          completedSets.forEach((set, idx) => {
                            const weight = set.weight || 0;
                            const reps = set.reps || 0;
                            if (weight > maxWeight || (weight === maxWeight && reps > maxRepsAtWeight)) {
                              maxWeight = weight;
                              maxRepsAtWeight = reps;
                              prSetIndex = idx;
                            }
                          });
                        }
                        
                        // Build inline sets string with PR highlight
                        const setsInline = hasCompletedSets
                          ? completedSets.map((set, idx) => {
                              const reps = set.reps || '-';
                              const weight = set.weight ? `${set.weight}kg` : '-';
                              const rpe = set.rpe ? ` @${set.rpe}` : '';
                              const isPR = idx === prSetIndex && set.weight > 0;
                              return `${idx + 1}: ${reps}×${weight}${rpe}${isPR ? ' ⭐' : ''}`;
                            }).join(' / ')
                          : `${exercise.sets || '-'} × ${exercise.reps || '-'}${exercise.weight ? ` @ ${exercise.weight}kg` : ''}`;
                        
                        return (
                          <div 
                            key={exIdx}
                            className="bg-blue-900/30 rounded px-3 py-2 border border-blue-500/20"
                          >
                            {/* Exercise Name */}
                            <p className="text-white font-medium text-sm">
                              {exercise.exercise_name_es || exercise.exercise_name || exercise.name || 'Ejercicio'}
                            </p>
                            
                            {/* All Sets Inline with PR highlight */}
                            <p className="text-blue-200 text-xs font-mono mt-0.5">
                              {setsInline}
                            </p>
                            
                            {/* Exercise Notes (if any) - compact */}
                            {(exercise.note || exercise.notes) && (
                              <p className="text-yellow-200/70 text-xs italic mt-1 truncate">
                                📝 {exercise.note || exercise.notes}
                              </p>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Trainer Comments - compact */}
                      {details.trainer_notes && (
                        <div className="px-3 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded">
                          <p className="text-yellow-300 text-xs font-medium">💬 Notas del entrenador:</p>
                          <p className="text-yellow-100 text-xs mt-0.5">{details.trainer_notes}</p>
                        </div>
                      )}
                      
                      {/* Client Feedback - compact */}
                      {details.client_feedback && (
                        <div className="px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded">
                          <p className="text-blue-300 text-xs font-medium">💭 Feedback:</p>
                          <p className="text-blue-100 text-xs mt-0.5">{details.client_feedback}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-blue-400 text-sm text-center py-4">
                      No hay detalles de ejercicios disponibles
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// EXTERNAL ACTIVITIES SUMMARY
// ============================================
const ExternalActivitySummary = ({ data }) => {
  if (!data || data.total_activities === 0) {
    return (
      <div className="glass-card p-4">
        <h3 className="text-white font-semibold mb-3">🏃 Actividades externas</h3>
        <p className="text-blue-400 text-sm">No hay actividades registradas</p>
      </div>
    );
  }
  
  const mostFrequentInfo = data.most_frequent_type ? getActivityInfo(data.most_frequent_type) : null;
  
  return (
    <div className="glass-card p-4">
      <h3 className="text-white font-semibold mb-3">🏃 Actividades externas</h3>
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-green-900/20 rounded-lg p-2 text-center">
          <p className="text-green-400 text-xs">Total</p>
          <p className="text-white font-bold">{data.total_activities}</p>
        </div>
        <div className="bg-blue-900/20 rounded-lg p-2 text-center">
          <p className="text-blue-400 text-xs">Tiempo</p>
          <p className="text-white font-bold">{Math.round(data.total_time_minutes / 60)}h</p>
        </div>
        {mostFrequentInfo && (
          <div className="bg-purple-900/20 rounded-lg p-2 text-center">
            <p className="text-purple-400 text-xs">Favorita</p>
            <p className="text-white text-lg">{mostFrequentInfo.emoji}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const TrainingMetricsDashboard = ({ userId, language, t, timeRangeOverride, isAdminView }) => {
  const [metrics, setMetrics] = useState(null);
  const [externalData, setExternalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(timeRangeOverride || '30d');
  const [showAllPRs, setShowAllPRs] = useState(false);
  const [progressionQuery, setProgressionQuery] = useState('');
  const [selectedExercise, setSelectedExercise] = useState(null); // For exercise modal

  // State for viewing workout details
  const [viewingWorkout, setViewingWorkout] = useState(null);
  const [loadingWorkout, setLoadingWorkout] = useState(false);

  // UX-3: auto-expand to full history ONLY when the selected range is empty
  // but historical sessions exist outside it (eliminates false "Sin datos").
  const [autoExpanded, setAutoExpanded] = useState(false);
  const autoExpandTriedRef = useRef(false);

  // Reset the auto-expand guard whenever the user deliberately changes range.
  useEffect(() => {
    autoExpandTriedRef.current = false;
    setAutoExpanded(false);
  }, [selectedPeriod]);

  // Update selectedPeriod when timeRangeOverride changes (for admin view)
  useEffect(() => {
    if (timeRangeOverride) {
      setSelectedPeriod(timeRangeOverride);
    }
  }, [timeRangeOverride]);
  
  // Fetch full workout details for viewing
  const fetchWorkoutDetails = async (workoutId) => {
    try {
      setLoadingWorkout(true);
      const response = await axios.get(`${API}/workouts/${workoutId}`);
      setViewingWorkout(response.data);
    } catch (error) {
      console.error('Error fetching workout details:', error);
      alert('Error al cargar los detalles del entrenamiento');
    } finally {
      setLoadingWorkout(false);
    }
  };

  const fetchMetricsData = useCallback(async () => {
    try {
      setLoading(true);
      const daysMap = { '7d': 7, '30d': 30, '90d': 90, 'all': 0 };
      const days = daysMap[selectedPeriod] || 30;

      const metricsBase = isAdminView
        ? `${API}/trainer/client-metrics/${userId}`
        : `${API}/training/client-metrics/${userId}`;

      const [metricsRes, externalRes] = await Promise.allSettled([
        axios.get(`${metricsBase}?time_range=${selectedPeriod}`),
        axios.get(`${API}/training/external-summary/${userId}?days=${days}`)
      ]);

      let metricsData = metricsRes.status === 'fulfilled' ? metricsRes.value.data : null;
      let expanded = false;

      // UX-3 auto-expand: selected range empty but history exists elsewhere.
      const isEmpty = !metricsData || (metricsData.summary?.total_workouts || 0) === 0;
      if (isEmpty && selectedPeriod !== 'all' && !autoExpandTriedRef.current) {
        autoExpandTriedRef.current = true;
        try {
          const allRes = await axios.get(`${metricsBase}?time_range=all`);
          if ((allRes.data?.summary?.total_workouts || 0) > 0) {
            metricsData = allRes.data;
            expanded = true;
          }
        } catch (e) {
          // non-blocking — fall back to the (empty) selected-range result
        }
      }

      if (metricsData) setMetrics(metricsData);
      setAutoExpanded(expanded);
      if (externalRes.status === 'fulfilled') {
        setExternalData(externalRes.value.data);
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, selectedPeriod, isAdminView]);

  useEffect(() => {
    if (userId) {
      fetchMetricsData();
    }
  }, [userId, selectedPeriod, fetchMetricsData]);

  // Handle PR card click - find full exercise data
  const handlePRClick = (record) => {
    // Find exercise details with full progress data - match by exercise_id (primary) or name (fallback)
    const exerciseDetail = metrics?.exercise_details?.find(e => 
      (record.exercise_id && e.exercise_id === record.exercise_id) || e.name === record.exercise
    );
    if (exerciseDetail) {
      setSelectedExercise(exerciseDetail);
    } else {
      // Fallback: create minimal exercise data
      setSelectedExercise({
        exercise_id: record.exercise_id,
        name: record.exercise,
        image_url: record.image_url,
        best_weight: record.weight,
        best_reps: record.reps,
        best_date: record.date,
        session_count: 1,
        progress: [{ date: record.date, weight: record.weight, reps: record.reps, volume: record.volume }]
      });
    }
  };

  // Handle Exercise Insight card click
  const handleExerciseClick = (exercise) => {
    setSelectedExercise(exercise);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white text-lg animate-pulse">Cargando métricas...</div>
      </div>
    );
  }

  const hasData = metrics && metrics.summary.total_workouts > 0;

  return (
    <div className="space-y-4">
      {/* HEADER - period selector (shown in both client and admin/trainer views) */}
      {!isAdminView ? (
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h2 className="text-xl font-bold text-white">Tu evolución</h2>
            <p className="text-blue-400/70 text-xs mt-0.5">
              Tu constancia, tu progresión y tu enfoque actual.
            </p>
          </div>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="bg-blue-900/30 text-white border border-blue-400/30 rounded-lg px-4 py-2 text-sm"
            data-testid="metrics-period-select"
          >
            {PERIOD_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      ) : (
        <div className="flex justify-end">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="bg-blue-900/30 text-white border border-blue-400/30 rounded-lg px-4 py-2 text-sm"
            data-testid="metrics-period-select"
          >
            {PERIOD_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* UX-3: subtle notice when range was auto-expanded to full history */}
      {autoExpanded && hasData && (
        <p
          className="text-blue-400/70 text-xs flex items-center gap-1.5"
          data-testid="metrics-autoexpand-notice"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400/60"></span>
          No hay sesiones en el periodo seleccionado · mostrando todo el histórico
        </p>
      )}

      {/* Empty State */}
      {!hasData && (
        <div className="text-center py-12">
          <div className="glass-card p-8 max-w-md mx-auto">
            <span className="text-6xl block mb-4">📊</span>
            <h3 className="text-xl font-semibold text-white mb-2">Sin datos de entrenamiento</h3>
            <p className="text-blue-300 mb-4">
              Completa tu primer entrenamiento para ver tus métricas aquí
            </p>
          </div>
        </div>
      )}

      {/* Main Content */}
      {hasData && (
        <>
          {/* ─────────────────────────────────────────────────────────────
              SECTION 1 — CONSTANCIA
              "Are you showing up?" — the operational truth.
              ───────────────────────────────────────────────────────────── */}
          <div className="space-y-3">
            <div>
              <h3 className="text-white font-semibold text-base">📅 Tu constancia</h3>
              <p className="text-blue-400/60 text-xs">
                Cada sesión cuenta. Aquí ves cómo se acumulan a lo largo del tiempo.
              </p>
            </div>

            {/* SUMMARY CARDS — pedagogical set only.
                Total Volume + Streak intentionally absent (see audit §5.7).
                Trainers can re-enable both via "Vista clásica" toggle. */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              <SummaryCard
                icon="🏋️"
                label="Entrenamientos"
                value={metrics.summary.total_workouts}
                color="green"
              />
              <SummaryCard
                icon="⏱"
                label="Tiempo efectivo"
                value={Math.round(metrics.summary.total_effective_time / 60) || '-'}
                unit="horas"
                color="blue"
              />
              <SummaryCard
                icon="📆"
                label="Frecuencia"
                value={metrics.summary.avg_weekly_frequency}
                unit="ses/sem"
                color="yellow"
              />
            </div>

            {/* CONSISTENCY CHART — Sessions over time (the truthful chart). */}
            <div className="grid grid-cols-1 gap-4">
              <div className="glass-card p-4">
                <h4 className="text-white font-semibold mb-3">📊 Sesiones por semana</h4>
                {metrics.charts.weekly_sessions && metrics.charts.weekly_sessions.length > 0 ? (
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={metrics.charts.weekly_sessions}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis
                        dataKey="week"
                        stroke="#9CA3AF"
                        fontSize={10}
                        tickFormatter={(val) => val.replace(/^\d+-W/, 'S')}
                      />
                      <YAxis stroke="#9CA3AF" fontSize={10} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                        formatter={(value) => [value, 'Sesiones']}
                      />
                      <Bar dataKey="sessions" fill="#34D399" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-blue-400 text-sm text-center py-8">Sin datos todavía</p>
                )}
              </div>
            </div>
          </div>

          {/* ─────────────────────────────────────────────────────────────
              SECTION 2 — PROGRESIÓN POR EJERCICIO (MERGED)
              Replaces both legacy "Récords personales" grid and
              "Progreso por ejercicio" insights — same data, one canonical
              card per exercise. Click → full progression modal.
              ───────────────────────────────────────────────────────────── */}
          {(metrics.exercise_details?.length > 0 || metrics.personal_records?.length > 0) && (
            <div className="glass-card p-4">
              <h3 className="text-white font-semibold text-base">🏆 Progresión por ejercicio</h3>
              <p className="text-blue-400/60 text-xs mb-3">
                Tu mejor lift por ejercicio y la tendencia de tus últimas sesiones. Haz clic para ver el historial.
              </p>
              <div className="relative mb-3">
                <Search className="w-4 h-4 text-purple-300/60 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  value={progressionQuery}
                  onChange={(e) => setProgressionQuery(e.target.value)}
                  placeholder="Buscar ejercicio…"
                  data-testid="progression-search"
                  className="w-full sm:w-72 bg-purple-950/40 border border-purple-500/25 rounded-lg pl-9 pr-8 py-2 text-sm text-white placeholder:text-purple-300/40 focus:outline-none focus:ring-1 focus:ring-purple-400 focus:border-purple-400"
                />
                {progressionQuery && (
                  <button
                    onClick={() => setProgressionQuery('')}
                    data-testid="progression-search-clear"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-purple-300/60 hover:text-white"
                    aria-label="Limpiar búsqueda"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {(() => {
                // Build a canonical list: prefer exercise_details (has trend +
                // session_count); fall back to personal_records for exercises
                // not yet in details. Dedupe by exercise_id.
                const detailsById = new Map();
                (metrics.exercise_details || []).forEach((e) => {
                  if (e.exercise_id) detailsById.set(e.exercise_id, e);
                });
                const fromPRs = (metrics.personal_records || []).filter(
                  (r) => r.exercise_id && !detailsById.has(r.exercise_id)
                );
                let cards = [
                  ...Array.from(detailsById.values()).map((exercise) => ({ exercise })),
                  ...fromPRs.map((record) => ({ record })),
                ];
                // Filter by search query (matches exercise name)
                const q = progressionQuery.trim().toLowerCase();
                if (q) {
                  cards = cards.filter((item) => {
                    const nm = (item.exercise?.name || item.record?.exercise || '').toLowerCase();
                    return nm.includes(q);
                  });
                }
                // When searching, show all matches; otherwise cap at 6 unless expanded.
                const visible = (q || showAllPRs) ? cards : cards.slice(0, 6);

                if (q && cards.length === 0) {
                  return (
                    <p className="text-blue-300/70 text-sm py-3" data-testid="progression-no-results">
                      No se encontraron ejercicios para “{progressionQuery}”.
                    </p>
                  );
                }

                return (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {visible.map((item, idx) => (
                        <ProgressionCard
                          key={(item.exercise?.exercise_id || item.record?.exercise_id || idx)}
                          exercise={item.exercise}
                          record={item.record}
                          onClick={() => {
                            if (item.exercise) handleExerciseClick(item.exercise);
                            else if (item.record) handlePRClick(item.record);
                          }}
                        />
                      ))}
                    </div>
                    {!q && cards.length > 6 && (
                      <button
                        onClick={() => setShowAllPRs(!showAllPRs)}
                        className="mt-3 text-purple-300 hover:text-purple-200 text-sm underline"
                        data-testid="metrics-show-all-progression"
                      >
                        {showAllPRs ? 'Ver menos' : `Ver todos los ejercicios (${cards.length})`}
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* EXTERNAL ACTIVITIES */}
          <ExternalActivitySummary data={externalData} />
          
          {/* RECENT WORKOUTS (P1) */}
          {metrics.recent_workouts && metrics.recent_workouts.length > 0 && (
            <RecentWorkoutsSection 
              workouts={metrics.recent_workouts}
              isLoading={loadingWorkout}
              onViewDetails={(workout) => {
                if (workout.id) {
                  fetchWorkoutDetails(workout.id);
                }
              }}
            />
          )}
        </>
      )}

      {/* ─────────────────────────────────────────────────────────────
          SECTION 3 — CICLO ACTUAL (TRAINER / ADMIN ONLY)
          Operational snapshot powered by the cycle_metrics engine.
          English copy per the operational-UI language boundary.
          Always rendered for trainers — even with no workout data —
          so the active block context is visible.
          ───────────────────────────────────────────────────────────── */}
      {isAdminView && <CycleSnapshotSection clientId={userId} />}

      {/* EXERCISE PROGRESSION MODAL */}
      {selectedExercise && (
        <ExerciseProgressionModal 
          exercise={selectedExercise}
          onClose={() => setSelectedExercise(null)}
        />
      )}
      
      {/* WORKOUT DETAILS MODAL — canonical unified report */}
      {viewingWorkout && (
        <WorkoutDetailsModal
          workout={viewingWorkout}
          clientName={metrics?.client_info?.name || 'Cliente'}
          date={viewingWorkout?.date}
          onClose={() => setViewingWorkout(null)}
          language={language || 'es'}
        />
      )}
    </div>
  );
};

export default TrainingMetricsDashboard;
